import React from "react";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import { ProductCheckboxList } from "components/ProductCheckboxList";
import { useDocumentV1Form } from "documentV1/hooks";
import { formatWrittenDayMonthYear } from "utilities";
import { Status, StatusDropdown } from "components";
import { ShowTitle } from "show/detail/components/ShowTitle";
import { getDocumentCoreApiExceptInvoice } from "utilities/apiUtils";
import { DocumentTypeWithoutInvoice, getDocumentRoutes } from "utilities/documentUtils";
import { getDocumentV1FormSections } from "documentV1/getDocumentV1FormSections";
import { getInvoiceV1FormSections } from "invoice/getInvoiceV1FormSections";

interface Props {
  viewModel: server.viewmodel.ShowNewDocumentFrontModel;
}

export const DocumentV1FromShowSection = (props: Props) => {
  const { viewModel } = props;
  const { show } = viewModel;

  const hook = useDocumentV1Form({
    documentType: viewModel.documentType,
    initialAddressee: viewModel.documentType === "purchaseAgreement" ? viewModel.artistCompany : show.promoter,
    initialContact: viewModel.documentType === "purchaseAgreement" ? show.attendees[0] : show.promoterContact,
    initialCurrency: show.currency,
    initialCostCenter: show.costCenter,
    initialExchangeRate: 1, // Exchange rate is unknown until bookkeeping is selected
    isDocumentFromShow: true,
    allArtistRiders: viewModel.artistRiders
  });
  const [selectedProducts, setSelectedProducts] = React.useState<number[]>(
    viewModel.products
      .filter(p => p.sales?.totalExVat.amount !== 0)
      .map(p => p.productId)
  );
  const [showStatus, setShowStatus] = React.useState(viewModel.show.status);

  const documentType = viewModel.documentType;

  const submit = async () => {
    if (documentType === "invoice") {
      return window.CoreApi.Invoice.CreateInvoiceV1FromShow({
        invoice: {
          exchangeRate: hook.exchangeRate,
          isDraft: hook.isDraft,
          ownerUserId: hook.owner.userId,
          invoiceDate: hook.sendDate.toDate(),
          bookkeepingId: hook.bookkeeping.bookkeepingId,
          addresseeCompanyId: hook.addressee.companyId,
          contactId: hook.contact?.contactId,
          currency: hook.currency,
          dueInDays: hook.dueInDays,
          bankAccountId: hook.bankAccount?.bankAccountId,
          costCenterId: hook.costCenter?.costCenterId,
          concerning: hook.concerning,
        } as Required<server.dto.CreateInvoiceV1Instruction>,
        selectedProducts: selectedProducts,
        showStatusId: showStatus.statusId,
        showId: show.showId,
      });
    } else {
      const createApi = getDocumentCoreApiExceptInvoice(documentType);

      return createApi.CreateDocumentV1FromShow({
        document: {
          exchangeRate: hook.exchangeRate,
          isDraft: hook.isDraft,
          ownerUserId: hook.owner.userId,
          sendDate: hook.sendDate.toDate(),
          bookkeepingId: hook.bookkeeping.bookkeepingId,
          addresseeCompanyId: hook.addressee.companyId,
          contactId: hook.contact?.contactId,
          currency: hook.currency,
          dueInDays: hook.dueInDays,
          concerning: hook.concerning,
          documentType: viewModel.documentType,
          artistRiderId: hook.artistRider?.artistRiderId,
        },
        selectedProducts: selectedProducts,
        showStatusId: showStatus.statusId,
        showId: show.showId,
      });
    }
  };

  const sections = documentType === "invoice"
    ? getInvoiceV1FormSections(hook, { alwaysShowCurrency: true })
    : getDocumentV1FormSections(hook, { documentType: documentType as DocumentTypeWithoutInvoice, alwaysShowCurrency: true });

  return <EditSectionTemplate
    submit={submit}
    redirectUrl={(itemId) => {
      const documentRoutes = getDocumentRoutes(documentType, itemId);
      return documentRoutes.detail;
    }}
    sections={[{
      key: "show",
      type: "regular",
      title: site.currentEnvironment.show.singular,
      fields: [{
        key: "showDate",
        label: RESX.DateTime.lblDate,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <div>{formatWrittenDayMonthYear(show.dateTime.startDisplay)}</div>
          </div>
        )
      }, {
        key: "showName",
        label: RESX.Show.resxShow,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <ShowTitle show={show} />
          </div>
        )
      }, {
        key: "showStatus",
        label: RESX.Status.resxStatus,
        renderEditNew: () => (
          show.isLocked
            ? <div className="main-section-template-fields-padding">
              <Status status={show.status} />
            </div>
            : <StatusDropdown
              mainArea="show"
              clientId={show.client.clientId}
              value={showStatus}
              onChange={setShowStatus}
              options={undefined}
            />
        )
      }]
    }, sections,
    {
      key: "products",
      type: "regular",
      title: RESX.Product.Products,
      fields: [{
        key: "products",
        label: RESX.Product.Products,
        renderEditNew: () => (
          viewModel.products.length === 0
            ? <div className="ui message red">{RESX.Document.TryingToAddDocumentWithoutAddingProductAndServiceItems}</div>
            : <ProductCheckboxList
              products={viewModel.products}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
            />
        )
      }]
    }]} />;
};

export default DocumentV1FromShowSection;
